import { MouseEvent, useContext, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Badge,
  Box,
  ClickAwayListener,
  Menu as MuiMenu,
  Slide,
  SwipeableDrawer,
  Typography,
  useMediaQuery
} from '@mui/material';
import { useSelector } from 'react-redux';
import theme from 'App.theme';
import { RootState } from 'storage/store';
import { Constant } from 'utils/consts';
import { Icon } from 'components/Icon';
import { DesktopMenuAnchorContext } from 'components/Menu/utils/desktopMenuAnchorContext';
import { MenuVisibilityContext } from 'components/Menu/utils/menuVisibilityContext';
import {
  selectBadgeNotifications,
  selectPaymentNotifications,
  selectRepaymentNotifications
} from 'storage/notifications/selectors';
import OverduePaymentsNotifications from './OverduePayments';
import RepaymentNotifications from './RepaymentNotifications';
import SharedDocuments from './SharedDocuments';

const BadgeAndMenu = () => {
  const { t } = useTranslation();
  const tabletOrBiggerDevice = useMediaQuery(theme.breakpoints.up('md'));

  const overDuePaymentNotifications = useSelector(selectPaymentNotifications);
  const repaymentNotifications = useSelector(selectRepaymentNotifications);
  const notifications = useSelector(selectBadgeNotifications);
  const { sharedDocumentsForUser } = useSelector(
    (state: RootState) => state.documents
  );

  const [isNewNotificationAvailable, setIsNewNotificationAvailable] =
    useState(false);
  const { notificationAnchor, setNotificationAnchor } = useContext(
    DesktopMenuAnchorContext
  );
  const { notificationsVisible, setNotificationsVisible, setVisible } =
    useContext(MenuVisibilityContext);
  if (!setNotificationAnchor || !setVisible) return null;

  useEffect(() => {
    const newNotificaitons = notifications.some(({ displayed }) => !displayed);
    const isAnyNewSharedDocumentNotification =
      (sharedDocumentsForUser &&
        sharedDocumentsForUser.some((document) => {
          const sharedDocuments = localStorage.getItem('sharedDocuments');
          const parsedData = sharedDocuments && JSON.parse(sharedDocuments);

          return (
            document.onDashboard &&
            (!parsedData || !parsedData.includes(+document.barcode))
          );
        })) ||
      false;

    setIsNewNotificationAvailable(
      newNotificaitons || isAnyNewSharedDocumentNotification
    );
  }, [notifications]);

  const isEmptyNotificationsList = useMemo(
    () =>
      notifications.length === Constant.zero &&
      (sharedDocumentsForUser?.length || 0) === Constant.zero,
    [notifications, sharedDocumentsForUser]
  );

  if (tabletOrBiggerDevice) {
    return (
      <>
        <ClickAwayListener onClickAway={() => setNotificationAnchor(null)}>
          <Badge
            color="warning"
            invisible={!isNewNotificationAvailable}
            variant="dot"
            overlap="circular"
            sx={{ marginRight: { md: '30px', lg: 0 }, cursor: 'pointer' }}
            onClick={(event: MouseEvent<HTMLElement>) => {
              setNotificationAnchor(
                notificationAnchor ? null : event.currentTarget
              );
              setVisible(false);
            }}
          >
            <Icon icon="bell" color="secondary" />
          </Badge>
        </ClickAwayListener>
        <MuiMenu
          anchorEl={notificationAnchor}
          elevation={2}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'left'
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'left'
          }}
          open={Boolean(notificationAnchor)}
          onClose={() => setNotificationAnchor(null)}
          sx={{
            display: { xs: 'none', md: 'block' },
            padding: '10px',
            marginTop: '22px',
            maxWidth: '425px',
            zIndex: 1300
          }}
          slots={{ transition: Slide }}
          disableAutoFocusItem
          disableScrollLock
          keepMounted
        >
          {isEmptyNotificationsList ? (
            <Box
              sx={{
                minWidth: '392px',
                padding: '40px 20px 30px'
              }}
            >
              <Typography variant="body2" sx={{ textAlign: 'center' }}>
                {t('NOTIFICATIONS_EMPTY_LIST')}
              </Typography>
            </Box>
          ) : (
            <Box sx={{ marginTop: { xs: 0, lg: '25px' } }}>
              {notifications ? (
                <OverduePaymentsNotifications
                  notificationArray={overDuePaymentNotifications}
                />
              ) : null}
              {repaymentNotifications ? (
                <RepaymentNotifications
                  notificationArray={repaymentNotifications}
                />
              ) : null}
              {sharedDocumentsForUser !== null &&
              sharedDocumentsForUser.length > Constant.zero ? (
                <SharedDocuments sharedDocuments={sharedDocumentsForUser} />
              ) : null}
            </Box>
          )}
        </MuiMenu>
      </>
    );
  }

  return (
    <>
      <Badge
        color="warning"
        variant="dot"
        overlap="circular"
        invisible={!isNewNotificationAvailable}
        sx={{ marginRight: '30px', cursor: 'pointer' }}
        onClick={() => {
          setNotificationsVisible!(!notificationsVisible);
          setVisible(false);
        }}
      >
        <Icon icon="bell" color="secondary" />
      </Badge>
      <SwipeableDrawer
        disableBackdropTransition
        anchor="top"
        open={notificationsVisible}
        onClose={() => setNotificationsVisible!(false)}
        onOpen={() => setNotificationsVisible!(true)}
        slotProps={{
          backdrop: {
            invisible: true
          }
        }}
        disableScrollLock
      >
        <Box
          sx={{
            padding: {
              xs: '24px 0 70px',
              md: '36px 49px'
            }
          }}
        >
          {isEmptyNotificationsList ? (
            <Box
              sx={{
                minWidth: '392px',
                padding: '40px 20px 30px'
              }}
            >
              <Typography variant="body2" sx={{ textAlign: 'center' }}>
                {t('NOTIFICATIONS_EMPTY_LIST')}
              </Typography>
            </Box>
          ) : (
            <div>
              {overDuePaymentNotifications.length > 0 ? (
                <OverduePaymentsNotifications
                  notificationArray={overDuePaymentNotifications}
                />
              ) : null}
              {repaymentNotifications.length > 0 ? (
                <RepaymentNotifications
                  notificationArray={repaymentNotifications}
                />
              ) : null}
              {sharedDocumentsForUser !== null &&
              sharedDocumentsForUser.length > Constant.zero ? (
                <SharedDocuments sharedDocuments={sharedDocumentsForUser} />
              ) : null}
            </div>
          )}
        </Box>
      </SwipeableDrawer>
    </>
  );
};

export default BadgeAndMenu;
